import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Modal, List, notification } from "antd";
import { StreamingResponseBody } from "api/generated";
import { AxiosResponse, isAxiosError } from "axios";

export function ExtraInfoBtn(
  title: string,
  content: string[],
): React.ReactNode {
  return (
    <Button
      type="text"
      icon={<InfoCircleOutlined />}
      onClick={() => {
        Modal.info({
          title,
          content: (
            <List>
              {content.map((line) => {
                return <List.Item key={line}>{line}</List.Item>;
              })}
            </List>
          ),
        });
      }}
    />
  );
}

export function handleAxiosError(err: unknown, message?: string) {
  if (isAxiosError(err)) {
    console.error(message, err);
    notification.error({
      message,
      description: err.response?.data?.message ?? err.message,
    });
  }
}

/**
 * Tipos de descarga soportados
 */
enum DownloadType {
  JSON = "json",
  PDF = "pdf",
  CSV = "csv",
  ZIP = "zip",
  UNKNOWN = "unknown",
}

/**
 * Configuración para cada tipo de descarga
 */
interface DownloadConfig {
  defaultName: string;
  mimeType: string;
  extension: string;
}

/**
 * Configuración por tipo de archivo tipada con el enum
 */
const DOWNLOAD_CONFIG: Record<DownloadType, DownloadConfig> = {
  [DownloadType.JSON]: {
    defaultName: "data.json",
    mimeType: "application/json",
    extension: ".json",
  },
  [DownloadType.PDF]: {
    defaultName: "document.pdf",
    mimeType: "application/pdf",
    extension: ".pdf",
  },
  [DownloadType.CSV]: {
    defaultName: "report.csv",
    mimeType: "text/csv",
    extension: ".csv",
  },
  [DownloadType.ZIP]: {
    defaultName: "archive.zip",
    mimeType: "application/zip",
    extension: ".zip",
  },
  [DownloadType.UNKNOWN]: {
    defaultName: "unknown.file",
    mimeType: "application/octet-stream",
    extension: "",
  },
};

/**
 * Extrae el nombre de archivo del encabezado Content-Disposition.
 * Si el encabezado no existe o no puede parsearse, retorna el valor por defecto indicado.
 */
function extractFileName(headers: unknown, fallback: string): string {
  const disposition = (headers as Record<string, string>)?.[
    "content-disposition"
  ];
  return disposition?.match(/filename="?([^";\n]+)"?/)?.[1] ?? fallback;
}

/**
 * Crea un blob según el tipo de datos
 */
function createBlob(data: unknown, type: DownloadType): Blob {
  const { mimeType } = DOWNLOAD_CONFIG[type];

  if (type === DownloadType.JSON) {
    return new Blob([JSON.stringify(data, null, 2)], { type: mimeType });
  }

  if (typeof data === "string" && type === DownloadType.PDF) {
    const bytes = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      bytes[i] = data.charCodeAt(i) & 0xff;
    }
    return new Blob([bytes], { type: mimeType });
  }

  if (data instanceof Blob) return data;
  if (data instanceof ArrayBuffer) return new Blob([data], { type: mimeType });

  return new Blob([data as BlobPart], { type: mimeType });
}

/**
 * Función principal de descarga
 */
function download(
  data: unknown,
  fileName: string,
  type: DownloadType = DownloadType.UNKNOWN,
): void {
  const config = DOWNLOAD_CONFIG[type];
  const finalName = fileName.toLowerCase().endsWith(config.extension)
    ? fileName
    : `${fileName}${config.extension}`;

  const url = URL.createObjectURL(createBlob(data, type));
  const link = document.createElement("a");
  link.href = url;
  link.download = finalName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Maneja la descarga de un archivo CSV a partir de una respuesta Axios.
 */
export function handleCsvDownload(
  response?: Partial<AxiosResponse<string[], unknown>>,
): void {
  if (!response) return;
  const fileName = extractFileName(
    response.headers,
    DOWNLOAD_CONFIG[DownloadType.CSV].defaultName,
  );
  download(response.data, fileName, DownloadType.CSV);
}

/**
 * Maneja la descarga de un archivo ZIP a partir de una respuesta Axios.
 */
export function handleZipDownload(
  response?: Partial<AxiosResponse<StreamingResponseBody, unknown>>,
): void {
  if (!response) return;
  const fileName = extractFileName(
    response.headers,
    DOWNLOAD_CONFIG[DownloadType.ZIP].defaultName,
  );
  download(response.data, fileName, DownloadType.ZIP);
}

/**
 * Maneja la descarga de un archivo JSON a partir de una respuesta Axios.
 * Convierte automáticamente el objeto JSON en un archivo descargable.
 */
export function handleJsonDownload(
  response?: Partial<AxiosResponse<unknown, unknown>>,
): void {
  if (!response) return;
  const fileName = extractFileName(
    response.headers,
    DOWNLOAD_CONFIG[DownloadType.JSON].defaultName,
  );
  download(response.data, fileName, DownloadType.JSON);
}

/**
 * Maneja la descarga de un archivo PDF a partir de una respuesta Axios.
 */
export function handlePdfDownload(
  response?: Partial<AxiosResponse<unknown, unknown>>,
): void {
  if (!response) return;
  const fileName = extractFileName(
    response.headers,
    DOWNLOAD_CONFIG[DownloadType.PDF].defaultName,
  );
  download(response.data, fileName, DownloadType.PDF);
}
